import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './ClubManagement.css';

import config from '../config';
const API_URL = config.API_URL;

interface Club {
    id: string;
    name: string;
    description: string;
    category: string;
    memberCount: number;
    logo: string;
}

interface Member {
    id: string;
    firstName: string;
    lastName: string;
    major: string;
    isOnline: boolean;
    joinedAt: string;
    role: string;
}

interface SearchUser {
    id: string;
    firstName: string;
    lastName: string;
    major: string;
    email: string;
}

const ClubManagement: React.FC = () => {
    const navigate = useNavigate();
    const [myClubs, setMyClubs] = useState<{ leading: Club[]; coLeading: Club[]; member: Club[] }>({
        leading: [],
        coLeading: [],
        member: []
    });
    const [selectedClub, setSelectedClub] = useState<any>(null);
    const [members, setMembers] = useState<Member[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newClub, setNewClub] = useState({
        name: '',
        description: '',
        category: 'Other',
        tags: ''
    });
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        if (currentUser.role !== 'club_leader' && currentUser.role !== 'admin') {
            toast.error('Access denied. Club leaders only.');
            navigate('/');
            return;
        }
        fetchMyClubs();
    }, []);

    const fetchMyClubs = async () => {
        try {
            const response = await axios.get(`${API_URL}/clubs/my/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setMyClubs(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch clubs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClubDetails = async (clubId: string) => {
        try {
            const response = await axios.get(`${API_URL}/clubs/${clubId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setSelectedClub(response.data.club);
                setMembers(response.data.club.members);
            }
        } catch (error) {
            toast.error('Failed to fetch club details');
        }
    };

    const handleCreateClub = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_URL}/clubs`, {
                ...newClub,
                tags: newClub.tags.split(',').map(t => t.trim()).filter(t => t)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                toast.success('Club created!');
                setShowCreateModal(false);
                setNewClub({ name: '', description: '', category: 'Other', tags: '' });
                fetchMyClubs();
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to create club');
        }
    };

    const handleSearchUsers = async (query: string) => {
        setSearchQuery(query);
        if (query.length < 2 || !selectedClub) return;

        try {
            const response = await axios.get(
                `${API_URL}/clubs/${selectedClub.id}/search-users?query=${query}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setSearchResults(response.data.users);
            }
        } catch (error) {
            console.error('Search failed:', error);
        }
    };

    const handleAddMember = async (userId: string) => {
        try {
            await axios.post(
                `${API_URL}/clubs/${selectedClub.id}/members`,
                { userId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Member added!');
            fetchClubDetails(selectedClub.id);
            setSearchResults([]);
            setSearchQuery('');
        } catch (error) {
            toast.error('Failed to add member');
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!window.confirm('Remove this member from the club?')) return;

        try {
            await axios.delete(
                `${API_URL}/clubs/${selectedClub.id}/members/${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Member removed');
            fetchClubDetails(selectedClub.id);
        } catch (error) {
            toast.error('Failed to remove member');
        }
    };

    const handleDeleteClub = async () => {
        if (!window.confirm('Are you sure you want to delete this club? This cannot be undone.')) return;

        try {
            await axios.delete(`${API_URL}/clubs/${selectedClub.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Club deleted');
            setSelectedClub(null);
            fetchMyClubs();
        } catch (error) {
            toast.error('Failed to delete club');
        }
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="club-management-page">
            {/* Navigation */}
            <nav className="club-nav">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                        <span className="leader-badge">Club Leader</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/chat" className="nav-link">Chat</Link>
                        <Link to="/clubs" className="nav-link active">My Clubs</Link>
                        <Link to="/profile" className="nav-link">Profile</Link>
                    </div>
                </div>
            </nav>

            <div className="club-container">
                <div className="club-header">
                    <div>
                        <h1>Club Management</h1>
                        <p>Create and manage your clubs</p>
                    </div>
                    <button 
                        className="btn-create-club"
                        onClick={() => setShowCreateModal(true)}
                    >
                        + Create New Club
                    </button>
                </div>

                <div className="club-layout">
                    {/* Clubs List */}
                    <div className="clubs-sidebar">
                        <h3>My Clubs ({myClubs.leading.length})</h3>
                        {myClubs.leading.length > 0 ? (
                            <ul className="clubs-list">
                                {myClubs.leading.map(club => (
                                    <li 
                                        key={club.id}
                                        className={`club-item ${selectedClub?.id === club.id ? 'selected' : ''}`}
                                        onClick={() => fetchClubDetails(club.id)}
                                    >
                                        <div className="club-avatar">
                                            {club.name[0]}
                                        </div>
                                        <div className="club-info">
                                            <span className="club-name">{club.name}</span>
                                            <span className="club-members">{club.memberCount} members</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="no-clubs">No clubs yet. Create one!</p>
                        )}
                    </div>

                    {/* Club Details */}
                    <div className="club-details">
                        {selectedClub ? (
                            <>
                                <div className="club-details-header">
                                    <div>
                                        <h2>{selectedClub.name}</h2>
                                        <p>{selectedClub.description}</p>
                                        <span className="club-category">{selectedClub.category}</span>
                                    </div>
                                    <button 
                                        className="btn-delete-club"
                                        onClick={handleDeleteClub}
                                    >
                                        Delete Club
                                    </button>
                                </div>

                                {/* Add Member */}
                                <div className="add-member-section">
                                    <h3>Add Member</h3>
                                    <input
                                        type="text"
                                        placeholder="Search users by name or email..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchUsers(e.target.value)}
                                        className="search-input"
                                    />
                                    {searchResults.length > 0 && (
                                        <ul className="search-results">
                                            {searchResults.map(user => (
                                                <li key={user.id} className="search-result-item">
                                                    <div>
                                                        <span className="user-name">
                                                            {user.firstName} {user.lastName}
                                                        </span>
                                                        <span className="user-email">{user.email}</span>
                                                    </div>
                                                    <button 
                                                        onClick={() => handleAddMember(user.id)}
                                                        className="btn-add"
                                                    >
                                                        + Add
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* Members List */}
                                <div className="members-section">
                                    <h3>Members ({members.length})</h3>
                                    <table className="members-table">
                                        <thead>
                                            <tr>
                                                <th>Member</th>
                                                <th>Major</th>
                                                <th>Role</th>
                                                <th>Joined</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.map(member => (
                                                <tr key={member.id}>
                                                    <td>
                                                        <div className="member-info">
                                                            <div className="member-avatar">
                                                                {member.firstName[0]}{member.lastName[0]}
                                                            </div>
                                                            <span>
                                                                {member.firstName} {member.lastName}
                                                                {member.isOnline && <span className="online-dot">🟢</span>}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{member.major}</td>
                                                    <td>
                                                        <span className={`role-badge ${member.role}`}>
                                                            {member.role}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                                                    <td>
                                                        <button 
                                                            onClick={() => handleRemoveMember(member.id)}
                                                            className="btn-remove"
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="no-selection">
                                <h3>👈 Select a club to manage</h3>
                                <p>Or create a new club to get started</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Club Modal */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Create New Club</h3>
                        <form onSubmit={handleCreateClub}>
                            <div className="form-group">
                                <label>Club Name</label>
                                <input
                                    type="text"
                                    value={newClub.name}
                                    onChange={(e) => setNewClub({...newClub, name: e.target.value})}
                                    required
                                    placeholder="e.g., NU Coding Club"
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={newClub.description}
                                    onChange={(e) => setNewClub({...newClub, description: e.target.value})}
                                    placeholder="What is your club about?"
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={newClub.category}
                                    onChange={(e) => setNewClub({...newClub, category: e.target.value})}
                                >
                                    <option value="Academic">Academic</option>
                                    <option value="Cultural">Cultural</option>
                                    <option value="Sports">Sports</option>
                                    <option value="Technology">Technology</option>
                                    <option value="Arts">Arts</option>
                                    <option value="Social">Social</option>
                                    <option value="Professional">Professional</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Tags (comma separated)</label>
                                <input
                                    type="text"
                                    value={newClub.tags}
                                    onChange={(e) => setNewClub({...newClub, tags: e.target.value})}
                                    placeholder="coding, web development, AI"
                                />
                            </div>
                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setShowCreateModal(false)}
                                    className="btn-cancel"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn-create">
                                    Create Club
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClubManagement;